import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { classBackupApi } from "./api";

// Manual mapping surface (§4/§R5): pick a live team row for an unmapped legacy
// staff and write teams.legacy_staff_id. Rejects a collision unless the admin
// explicitly reassigns.
type Props = {
  legacyId: number;
  legacyName: string | null;
  onClose: () => void;
  onMapped: () => void;
};

type Candidate = {
  id: number;
  teamId: string | null;
  teamFirstName: string | null;
  teamLastName: string | null;
  teamEmail: string | null;
  legacy_staff_id: number | null;
};

export default function MapStaffDialog({ legacyId, legacyName, onClose, onMapped }: Props) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState<Candidate[]>([]);
  const [selectedId, setSelectedId] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);
  const [needsForce, setNeedsForce] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const h = setTimeout(async () => {
      const res = await classBackupApi.liveStaffSearch(q);
      setItems(res.items);
    }, 250);
    return () => clearTimeout(h);
  }, [q]);

  const submit = async (force: boolean) => {
    if (!selectedId) return;
    setLoading(true); setError(null); setNeedsForce(false);
    try {
      const r = await classBackupApi.mapStaff(legacyId, Number(selectedId), force);
      if ((r as any).ok) { onMapped(); return; }
      if ((r as any).requires_confirmation) {
        setNeedsForce(true);
        setError((r as any).error);
      } else {
        setError((r as any).error || "Mapping failed.");
      }
    } catch (e: any) {
      if (e.status === 409 && e.body?.requires_confirmation) {
        setNeedsForce(true);
        setError(e.body.error);
      } else {
        setError(e.message || String(e));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Map legacy staff → live team</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          Legacy staff <b>{legacyName || `#${legacyId}`}</b> (id {legacyId}) needs a live team mapping.
          Pick the correct live team row; the system will write <code>teams.legacy_staff_id = {legacyId}</code>.
        </Typography>

        <Stack spacing={1.5} mt={1}>
          <TextField label="Search live team by name / email / staff id" size="small"
            value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          <TextField label="Live team" size="small" select
            value={selectedId} onChange={(e) => setSelectedId(e.target.value as any)}>
            {items.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {(c.teamFirstName || "") + " " + (c.teamLastName || "")} — {c.teamId || ""} {c.teamEmail ? ` · ${c.teamEmail}` : ""}
                {c.legacy_staff_id ? ` (currently mapped to legacy ${c.legacy_staff_id})` : ""}
              </MenuItem>
            ))}
            {items.length === 0 && <MenuItem value="" disabled>No matches</MenuItem>}
          </TextField>

          {error && <Alert severity={needsForce ? "warning" : "error"}>{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {needsForce ? (
          <Button color="warning" variant="contained" disabled={!selectedId || loading}
            onClick={() => submit(true)}>Reassign anyway</Button>
        ) : (
          <Button color="primary" variant="contained" disabled={!selectedId || loading}
            onClick={() => submit(false)}>Save mapping</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
